import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {DEBOUNCE_MS} from 'src/constants';
import {ACCESS_MANAGEMENT} from 'src/hooks/access-resource-constant';
import {useVendors} from 'src/hooks/use-vendors';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {usePageTitle} from 'src/hooks/usePageTitle';
import {getAccessManagementTools} from 'src/openapi/admin/apis/ToolApi';
import {SearchDirection} from 'src/openapi/admin/models/SearchDirection';
import {useAppDispatch, useAppSelector} from 'src/store';

import BoxLoading from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {FilterInput} from '../../components/filter-input';

import {AccessManagementTable} from './components/access-management-table';
import {AddUserDialog} from './components/add-user/add-user-dialog';
import {useAvailableRoles} from './hooks/use-available-roles';
import {useAccessManagementData} from './hooks/useAccessManagementData';
import {
    accessManagementUsersEmailFieldSelector,
    accessManagementUsersRoleFieldSelector,
    accessManagementUsersSortCriteriaSelector,
    accessManagementUsersToolFieldSelector,
    accessManagementUsersVendorFieldSelector,
    changeEmail,
    changeRole,
    changeSortCriteria,
    changeTool,
    changeVendor,
    clear,
} from './service/access-management-users.slice';

import {mainHeader} from './access-management.module.styl';

export const AccessManagement: React.FC = () => {
    usePageTitle('Client Portal: Access Management');
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const emailFilter = useAppSelector(accessManagementUsersEmailFieldSelector) ?? '';
    const roleId = useAppSelector(accessManagementUsersRoleFieldSelector);
    const vendorId = useAppSelector(accessManagementUsersVendorFieldSelector);
    const toolId = useAppSelector(accessManagementUsersToolFieldSelector);
    const sortCriteria = useAppSelector(accessManagementUsersSortCriteriaSelector);
    const {hasAccessToResource} = useCurrentUser();

    const tools = useAppSelector(state => state.accessManagement.tools);
    const canAccess = hasAccessToResource(ACCESS_MANAGEMENT.TOOL_ACCESS);

    useEffect(() => {
        return () => {
            dispatch(clear());
        };
    }, [dispatch]);

    useEffect(() => {
        if (canAccess && tools.length === 0) {
            apiDispatch(getAccessManagementTools());
        }
    }, [canAccess, tools.length, apiDispatch]);

    const handleEmailChange = useCallback((v: string) => {
        dispatch(changeEmail(v.toLowerCase().trim()));
    }, [dispatch]);

    const handleEmailClear = useCallback(() => {
        dispatch(changeEmail(''));
    }, [dispatch]);

    const handleEmailSortChange = useCallback((d: 'ASC' | 'DESC') => {
        dispatch(changeSortCriteria({
            ...sortCriteria,
            sortDirection: d === 'ASC' ? SearchDirection.ASC : SearchDirection.DESC,
        }));
    }, [dispatch, sortCriteria]);

    const handleRoleChange = useCallback((id?: string) => {
        dispatch(changeRole(id));
    }, [dispatch]);

    const handleVendorChange = useCallback((id?: string) => {
        dispatch(changeVendor(id));
    }, [dispatch]);

    const handleToolChange = useCallback((id?: string) => {
        dispatch(changeTool(id));
    }, [dispatch]);

    const handleClearAllFilters = useCallback(() => {
        dispatch(clear());
    }, [dispatch]);

    const {users, loading} = useAccessManagementData();
    const {availableRoles} = useAvailableRoles();
    const canCreateUsers = availableRoles.length > 0;
    const {vendors} = useVendors();
    const hasActiveFilter = emailFilter.length > 0
        || roleId !== undefined || vendorId !== undefined || toolId !== undefined;

    const handleAddUserClick = useCallback(() => {
        setShowAddUserDialog(true);
    }, []);

    const handleCloseAddUserDialog = useCallback(() => {
        setShowAddUserDialog(false);
    }, []);

    if (!canAccess) {
        return (
            <Group direction="v" gap="l" alignItems="center" justify="center" style={{minHeight: '80vh'}}>
                <Typography variant="heading-level-1">Access Management</Typography>
                <Typography variant="body-level-1">You do not have access to this module.</Typography>
            </Group>
        );
    }

    return (
        <Group direction="v">
            <Group style={{padding: 16, paddingBottom: 0}} direction="v">
                <Typography
                    className={mainHeader}
                    variant="heading-level-1">
                    Access Management
                </Typography>
                <Group direction="h" justify="space-between" gap="m" style={{padding: 8}}>
                    <FilterInput
                        id="access-mgmt-email-filter"
                        value={emailFilter}
                        onChange={handleEmailChange}
                        onClear={handleEmailClear}
                        timeout={DEBOUNCE_MS}
                        width={420}
                        placeholder="Search users by email"
                    />
                    {canCreateUsers ?
                            (
                                <Button onClick={handleAddUserClick} variant="Type.PRIMARY">
                                    Add User
                                </Button>
                            ) : null}
                </Group>
            </Group>
            <BoxLoading loading={loading}>
                <AccessManagementTable
                    users={users}
                    hasActiveFilter={hasActiveFilter}
                    emailSortDir={sortCriteria.sortDirection === SearchDirection.ASC ? 'ASC' : 'DESC'}
                    onEmailSortChange={handleEmailSortChange}
                    roleId={roleId}
                    onRoleIdChange={handleRoleChange}
                    vendorId={vendorId}
                    onVendorIdChange={handleVendorChange}
                    toolId={toolId}
                    onToolIdChange={handleToolChange}
                    onClearFilters={handleClearAllFilters}
                    roles={availableRoles}
                    vendors={vendors}
                    tools={tools}
                />
            </BoxLoading>
            <AddUserDialog
                showDialog={showAddUserDialog}
                closeDialog={handleCloseAddUserDialog}
                vendors={vendors}
            />
        </Group>
    );
};
